import { type RefObject, useCallback, useEffect, useRef } from 'react';

import { getMaxScrollLeft, useNavScrollState } from './use-nav-scroll-state';

const FADE_AREA_WIDTH = 56;
const ARROW_BUTTON_WIDTH = 32;
const INITIAL_SCROLL_DELAY = 150;
const ITEMS_RETRY_DELAY = 50;

interface UseScrollableNavProps {
  autoScrollToSelected?: boolean;
  navItemsRef: RefObject<HTMLUListElement | null>;
  navigationKey?: string;
}

interface ScrollableNavReturn {
  scrollNav: (direction: 'left' | 'right') => void;
  showLeftArrow: boolean;
  showRightArrow: boolean;
}

export const useScrollableNav = ({
  autoScrollToSelected = false,
  navItemsRef,
  navigationKey,
}: UseScrollableNavProps): ScrollableNavReturn => {
  const { showLeftArrow, showRightArrow } = useNavScrollState({ navItemsRef });

  const scrollNav = useCallback(
    (direction: 'left' | 'right') => {
      const navItems = navItemsRef.current;
      if (!navItems) return;

      const scrollStep = getAverageItemWidth(navItems);
      if (!scrollStep) return;

      const currentScroll = navItems.scrollLeft;
      const maxScroll = getMaxScrollLeft(navItems);
      const target =
        direction === 'left'
          ? Math.max(0, currentScroll - scrollStep)
          : Math.min(maxScroll, currentScroll + scrollStep);

      navItems.scrollTo({ left: target, behavior: 'smooth' });
    },
    [navItemsRef],
  );

  useAutoScrollToSelected({
    enabled: autoScrollToSelected,
    navigationKey,
    navItemsRef,
    showLeftArrow,
  });

  return { showLeftArrow, showRightArrow, scrollNav };
};

const getListItems = (navItems: HTMLUListElement) =>
  Array.from(navItems.children).filter((child): child is HTMLLIElement => child instanceof HTMLLIElement);

const getAverageItemWidth = (navItems: HTMLUListElement) => {
  const items = getListItems(navItems);
  if (!items.length) return 0;

  const totalWidth = items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
  return Math.ceil(totalWidth / items.length);
};

interface SelectedNavItemResult {
  itemsReady: boolean;
  selectedItem: HTMLLIElement | null;
}

const findSelectedNavItem = (navItems: HTMLUListElement): SelectedNavItemResult => {
  const items = getListItems(navItems);
  if (!items.length) {
    return { selectedItem: null, itemsReady: false };
  }

  const selectedItem = items.find(item => item.dataset.navSelected === 'true') ?? null;
  return { selectedItem, itemsReady: true };
};

const computeTargetScroll = ({
  itemLeft,
  itemWidth,
  navItems,
  showLeftArrow,
}: {
  itemLeft: number;
  itemWidth: number;
  navItems: HTMLUListElement;
  showLeftArrow: boolean;
}) => {
  const arrowOffset = showLeftArrow ? ARROW_BUTTON_WIDTH : 0;
  const target = itemLeft - navItems.clientWidth / 2 + itemWidth / 2 - arrowOffset;
  const maxScroll = getMaxScrollLeft(navItems);
  return Math.max(0, Math.min(target, maxScroll));
};

interface AutoScrollOptions {
  enabled?: boolean;
  navItemsRef: RefObject<HTMLUListElement | null>;
  navigationKey?: string;
  showLeftArrow: boolean;
}

const useAutoScrollToSelected = ({ enabled, navItemsRef, navigationKey, showLeftArrow }: AutoScrollOptions) => {
  const lastScrolledKeyRef = useRef<string | null>(null);
  const showLeftArrowRef = useRef(showLeftArrow);
  showLeftArrowRef.current = showLeftArrow;

  useEffect(() => {
    if (!enabled) return;

    const scrollKey = navigationKey ?? '__initial__';
    if (lastScrolledKeyRef.current === scrollKey) return;

    const navItems = navItemsRef.current;
    if (!navItems) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const performScroll = (): boolean => {
      const { itemsReady, selectedItem } = findSelectedNavItem(navItems);

      if (!itemsReady) return false;

      lastScrolledKeyRef.current = scrollKey;

      if (!selectedItem) return true;

      const containerRect = navItems.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();
      const currentScroll = navItems.scrollLeft;
      const itemLeft = itemRect.left - containerRect.left + currentScroll;
      const itemRight = itemLeft + itemRect.width;
      const visibleLeft = currentScroll;
      const visibleRight = currentScroll + navItems.clientWidth - FADE_AREA_WIDTH;

      if (itemLeft >= visibleLeft && itemRight <= visibleRight) return true;

      const targetScroll = computeTargetScroll({
        navItems,
        itemLeft,
        itemWidth: itemRect.width,
        showLeftArrow: showLeftArrowRef.current,
      });

      navItems.scrollTo({ left: targetScroll, behavior: 'smooth' });
      return true;
    };

    timeoutId = setTimeout(() => {
      if (performScroll()) return;
      timeoutId = setTimeout(performScroll, ITEMS_RETRY_DELAY);
    }, INITIAL_SCROLL_DELAY);

    return () => clearTimeout(timeoutId);
  }, [enabled, navigationKey, navItemsRef]);
};
